import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Paper,
  Toolbar,
  Typography,
} from '@mui/material';
import {  
  Person as PersonIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  MenuBook as MenuBookIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const Sidebar = ({ open, onToggle, mobileOpen, onMobileToggle }) => {
  const { user } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!user) {
    return null;
  }

  const menuItems = [
    { path: '/browser-info', icon: <PersonIcon />, label: 'Persönliches Lagebild' },
    { path: '/hack-story', icon: <MenuBookIcon />, label: 'Hack Story' },
    { path: '/digitale-selbstverteidigung', icon: <SecurityIcon />, label: 'Digitale Selbstverteidigung' },
  ];

  // Handle menu item click - close mobile drawer on navigation
  const handleMenuItemClick = () => {
    if (isMobile && mobileOpen) {
      onMobileToggle();
    }
  };

  const drawer = (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
      height: '100%',
      color: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Mobile Header with Close Button */}
      {isMobile && (
        <Toolbar sx={{ 
          minHeight: '64px !important',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
        }}>
          <Typography variant="h6" sx={{ 
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '1.1rem',
          }}>
            Menü
          </Typography>
          <IconButton
            onClick={onMobileToggle}
            sx={{
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#42a5f5',
              },
            }}
            aria-label="close menu"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      )}

      <List sx={{ 
        px: { xs: 2, sm: 1 }, 
        pt: { xs: 2, sm: 2 },
        flex: 1,
        overflowY: 'auto',
      }}>
        {menuItems.map((item) => (
          <ListItem 
            key={item.path}
            button 
            component={Link} 
            to={item.path}
            selected={location.pathname === item.path}
            onClick={handleMenuItemClick}
            sx={{ 
              background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.8) 0%, rgba(15, 15, 15, 0.8) 100%)',
              borderRadius: 2,
              mb: 1,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.3s ease-in-out',
              justifyContent: open || isMobile ? 'flex-start' : 'center',
              px: open || isMobile ? 2 : 0,
              py: { xs: 2, sm: 1.5 }, // Larger touch targets on mobile
              minHeight: { xs: '56px', sm: '48px' }, // Minimum touch target size
              '&:active': {
                transform: 'scale(0.98)', // Visual feedback on mobile tap
              },
              '&:hover': {
                transform: open || isMobile ? 'translateX(4px)' : 'none',
                boxShadow: '0 6px 24px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(66, 165, 245, 0.3)',
              },
              '& .MuiListItemText-primary': { 
                opacity: open || isMobile ? 1 : 0,
                transition: 'opacity 0.3s',
                color: '#ffffff',
                fontWeight: 500,
                fontSize: { xs: '1rem', sm: '0.875rem' }, // Larger text on mobile
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(33, 150, 243, 0.15)',
                borderLeft: (open || isMobile) ? '4px solid #2196f3' : 'none',
                border: '1px solid rgba(66, 165, 245, 0.3)',
                '& .MuiListItemIcon-root': {
                  color: '#42a5f5',
                },
                '& .MuiListItemText-primary': {
                  color: '#42a5f5',
                }
              }
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: (open || isMobile) ? 56 : collapsedDrawerWidth, 
              justifyContent: 'center',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              '& svg': {
                fontSize: { xs: '28px', sm: '24px' }, // Larger icons on mobile
              },
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer - Visible in all mobile orientations */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{
          keepMounted: true, // Better performance on mobile
        }}
        anchor="left"
        sx={{
          display: { xs: 'block', md: 'none' }, // Show on all mobile sizes (xs and sm)
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)', // Darker backdrop for better focus
          },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: { xs: '280px', sm: '300px' }, // Responsive width for portrait and landscape
            maxWidth: { xs: '85vw', sm: '320px' }, // Don't exceed viewport width
            background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(33, 150, 243, 0.4)',
            backdropFilter: 'blur(10px)',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: open ? drawerWidth : collapsedDrawerWidth,
            overflowX: 'hidden',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 20px rgba(33, 150, 243, 0.3)',
            backdropFilter: 'blur(10px)',
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
        open={open}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{ 
              color: '#ffffff',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#42a5f5'
              }
            }}
          >
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>
        {drawer}
      </Drawer>
    </>
  );
};

export default Sidebar;
export { drawerWidth, collapsedDrawerWidth };

